import { Box, Typography } from '@mui/material'
import { GridColDef } from '@mui/x-data-grid-premium'
import { useMemo } from 'react'
import { Table } from 'Common/MUITableV2'
import { GridSkeleton } from './GridSkeleton.view'
import { autoHeight } from 'Common/utils/utils'
import { DAY_LABELS, formatPct, useUtilisationData } from './Utilisation.data'
import type { AssetType, DailyUtilisationRow } from './Utilisation.types'

type Props = {
  schedulerId: string
  iteration: string
}

type PivotedRow = {
  id: string
  asset_type: AssetType
  asset_name: string
  measure: string
  mon: number
  tue: number
  wed: number
  thu: number
  fri: number
  sat: number
  sun: number
}

const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const

const ASSET_TYPE_LABEL: Record<AssetType, string> = {
  kitchen: 'Kitchen',
  line: 'Line',
  cip: 'CIP',
}

const pivot = (rows: DailyUtilisationRow[]): PivotedRow[] => {
  const byKey = new Map<string, PivotedRow>()
  for (const r of rows) {
    const key = `${r.asset_type}|${r.asset_name}`
    let pivoted = byKey.get(key)
    if (!pivoted) {
      pivoted = {
        id: key,
        asset_type: r.asset_type,
        asset_name: r.asset_name,
        measure: 'Utilisation',
        mon: 0,
        tue: 0,
        wed: 0,
        thu: 0,
        fri: 0,
        sat: 0,
        sun: 0,
      }
      byKey.set(key, pivoted)
    }
    const dayKey = DAY_KEYS[r.day_of_week]
    if (dayKey) pivoted[dayKey] = r.utilisation_pct
  }
  return Array.from(byKey.values()).sort((a, b) => {
    const order: AssetType[] = ['kitchen', 'line', 'cip']
    const t = order.indexOf(a.asset_type) - order.indexOf(b.asset_type)
    return t !== 0 ? t : a.asset_name.localeCompare(b.asset_name)
  })
}

const COLS: GridColDef<PivotedRow>[] = [
  {
    field: 'asset_type',
    headerName: 'Asset Type',
    flex: 1,
    minWidth: 110,
    valueFormatter: (value: AssetType) => ASSET_TYPE_LABEL[value] ?? value,
  },
  { field: 'asset_name', headerName: 'Asset', flex: 1, minWidth: 110 },
  { field: 'measure', headerName: 'Measure', flex: 1, minWidth: 110 },
  ...DAY_KEYS.map<GridColDef<PivotedRow>>((key, idx) => ({
    field: key,
    headerName: DAY_LABELS[idx],
    type: 'number',
    flex: 1,
    minWidth: 90,
    valueFormatter: (value: number) => formatPct(value),
  })),
]

export const DailyUtilisationView = ({ schedulerId, iteration }: Props) => {
  const { data, isLoading, error } = useUtilisationData(schedulerId, iteration)

  const rows = useMemo(
    () => (data ? pivot(data.daily_utilisation) : []),
    [data]
  )

  // Grid skeleton, not a spinner: same columns, same height, so the
  // real table replaces it without the layout moving.
  if (isLoading) return <GridSkeleton columns={COLS as GridColDef[]} />
  if (error || !data) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">
          Could not load utilisation data
          {error instanceof Error ? `: ${error.message}` : ''}.
        </Typography>
      </Box>
    )
  }

  return (
    <Box className="table-container-mui">
      <Table
        ref={(el: HTMLDivElement | null) => el && autoHeight(el)}
        rows={rows}
        columns={COLS as GridColDef[]}
        density="compact"
        disableRowSelectionOnClick
        getRowClassName={(p) =>
          (p.indexRelativeToCurrentPage ?? 0) % 2 === 0
            ? 'row--even'
            : 'row--odd'
        }
        initialState={{
          pagination: { paginationModel: { pageSize: 100 } },
        }}
        pageSizeOptions={[25, 50, 100]}
      />
    </Box>
  )
}
